// Pure layout helpers for the channel day view (feature 006-channel-day-view).
// No React / Redux: channel-column resolution, time placement and overlap-lane assignment.
import {PeerActivityType} from "../../redux/Content/PeerActivity/types";
import {ContentPeerChannelType} from "../../redux/Content/PeerChannel/types";

export interface ChannelColumn {
    uuid: string;
    name: string;
    platformCode: string | null;
    platformName: string;
    count: number;
}

// A post reduced to what the layout needs; `key` identifies it back in the board.
export interface DayPostInput {
    key: string;
    startMin: number; // minutes from midnight
    endMin: number;   // minutes from midnight (>= startMin)
}

export interface DayPostLayout {
    key: string;
    topPx: number;
    heightPx: number;
    lane: number;       // 0-based column within its overlap cluster
    laneCount: number;  // lanes in that cluster
}

function channelPlatformCode(ch: any): string | null {
    return ch?.handler?.code || ch?.connector?.handler?.code || null;
}
function channelPlatformName(ch: any): string {
    return ch?.handler?.name || ch?.connector?.handler?.name || "";
}

// All managed channels as columns (incl. channels with no posts), unioned with any channel
// appearing on the day's activities that is not already present. Stable order by name.
// `managed` keys on `uuid`, the same identity as `activity.channel.uuid` (feature 006, U1).
export function resolveChannelColumns(
    managed: ContentPeerChannelType[],
    activities: PeerActivityType[],
): ChannelColumn[] {
    const byUuid = new Map<string, ChannelColumn>();

    (managed || []).forEach((ch) => {
        const uuid = ch?.uuid || "";
        if (!uuid || byUuid.has(uuid)) return;
        byUuid.set(uuid, {
            uuid,
            name: ch?.name || "",
            platformCode: channelPlatformCode(ch),
            platformName: channelPlatformName(ch),
            count: 0,
        });
    });

    (activities || []).forEach((a) => {
        const ch: any = a.channel;
        const uuid = ch?.uuid || "";
        if (!uuid) return;
        if (!byUuid.has(uuid)) {
            byUuid.set(uuid, {
                uuid,
                name: ch?.name || "",
                platformCode: channelPlatformCode(ch),
                platformName: channelPlatformName(ch),
                count: 0,
            });
        }
        byUuid.get(uuid)!.count += 1;
    });

    return Array.from(byUuid.values()).sort((a, b) =>
        a.name.localeCompare(b.name) || a.uuid.localeCompare(b.uuid));
}

// Place posts in one channel column: vertical position by time, side-by-side lanes for overlaps.
export function layoutChannelPosts(posts: DayPostInput[], hourPx: number, minChipPx = 44): DayPostLayout[] {
    const sorted = [...posts].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
    const out: DayPostLayout[] = [];

    let i = 0;
    while (i < sorted.length) {
        // Build a cluster of time-overlapping posts.
        const cluster: DayPostInput[] = [sorted[i]];
        let clusterEnd = sorted[i].endMin;
        let j = i + 1;
        while (j < sorted.length && sorted[j].startMin < clusterEnd) {
            cluster.push(sorted[j]);
            if (sorted[j].endMin > clusterEnd) clusterEnd = sorted[j].endMin;
            j++;
        }

        // Assign each post the lowest lane whose last post has ended.
        const laneEnds: number[] = [];
        const laneOf = new Map<string, number>();
        cluster.forEach((p) => {
            let lane = laneEnds.findIndex((end) => end <= p.startMin);
            if (lane === -1) { lane = laneEnds.length; laneEnds.push(p.endMin); }
            else laneEnds[lane] = p.endMin;
            laneOf.set(p.key, lane);
        });
        const laneCount = laneEnds.length;

        cluster.forEach((p) => {
            out.push({
                key: p.key,
                topPx: (p.startMin / 60) * hourPx,
                heightPx: Math.max(minChipPx, ((p.endMin - p.startMin) / 60) * hourPx),
                lane: laneOf.get(p.key)!,
                laneCount,
            });
        });

        i = j;
    }

    return out;
}

// Minutes-from-midnight for the current time (local — same frame as activity placement).
export function nowMinutes(now: Date): number {
    return now.getHours() * 60 + now.getMinutes();
}

// Vertical pixel offset for a minutes-from-midnight value.
export function minutesToPx(min: number, hourPx: number): number {
    return (min / 60) * hourPx;
}
